/* eslint-disable react-hooks/exhaustive-deps */
import { AxiosError } from 'axios';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

export type GetDataResponse<T> = {
  loading?: boolean;
  error?: string;
  data: T;
  refetch: () => Promise<void>;
  enableQuery: Dispatch<SetStateAction<boolean>>;
};

/**
 * Helper hook to fetch async data
 * @param callback Function to fetch resource and returns Promise<T>
 * @returns { loading: boolean, error: string, data: T, refetch: () => Promise<void> }
 */
export function useQuery<T>(
  callback: () => Promise<T>,
  enabled = true,
  runOnInit = true,
  onError?: (e: AxiosError) => void,
): GetDataResponse<T> {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [data, setData] = useState<T>();
  const [isEnabled, setEnabled] = useState(enabled);
  const initialized = useRef(false);

  const getData = async (ignoreCheck = false) => {
    if (loading || (!ignoreCheck && !enabled)) return;
    setLoading(true);
    setData(undefined);
    setError(undefined);
    try {
      const result = await callback();
      setData(result);
    } catch (err) {
      const errorMessage = (err as Error)?.message ?? err;
      setError(errorMessage);
      if (onError) onError(err as AxiosError);
    } finally {
      setLoading(false);
    }
  };

  const refetch = () => {
    getData(true);
  };

  useEffect(() => {
    if (initialized.current === false && runOnInit === false) {
      initialized.current = true;
      return;
    }

    if (isEnabled) {
      getData();
    }
  }, [isEnabled]);

  return {
    loading,
    error,
    data,
    refetch,
    enableQuery: setEnabled,
  } as GetDataResponse<T>;
}
